import { FieldArray, Form, Formik, useField, useFormikContext } from "formik";
import { Plus, Trash2 } from "lucide-react";
import * as Yup from "yup";
import { yearRange } from "../validation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Client } from "@/features/clients/types";
import type {
  Form2848Representative,
  FormSubmission,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikCheckbox } from "./formik-controls";
import { PoaRepresentativesTable } from "./poa-representatives-table";

// --- Michigan Form 151 editable payload -------------------------------------
// snake_case keys mirror `apps/forms/mappings/form_mipoa.py` `build_defaults`.
// Michigan is a single-representative POA with revoke (Part 2), a single
// authority matter row (Part 4), and up to three copy rows (Part 5).

export type MipoaGenerateFor = "taxpayer" | "spouse" | "jointly";

export interface MipoaCopyRow {
  tax_type: string;
  tax_period: string;
}

export interface MipoaAuthority {
  receive_inspect: boolean;
  make_presentation: boolean;
  sign_returns: boolean;
  enter_agreements: boolean;
}

export interface Form_mipoa_Data {
  generate_for: MipoaGenerateFor;
  representatives: Form2848Representative[];
  authorization_start_date: string;
  authorization_expiration_date: string;
  representative_contact_name: string;
  revoke_all: boolean;
  revoke_matters: boolean;
  revoked_type: string;
  revoked_years: string;
  revoke_copies: boolean;
  revoked_type_2: string;
  revoked_years_2: string;
  authority: MipoaAuthority;
  auth_type: string;
  auth_years: string;
  send_copies: boolean;
  copy_rows: MipoaCopyRow[];
  signer: string;
  signer_title: string;
  signature_date: string;
  spouse_signature_date: string;
}

/** Part 5 (send copies) fits three tax-type / tax-period rows on the form. */
const MAX_COPY_ROWS = 3;

const GENERATE_FOR_OPTIONS = [
  { value: "taxpayer", label: "Taxpayer" },
  { value: "spouse", label: "Spouse" },
  { value: "jointly", label: "Jointly" },
] as const;

const AUTHORITY_OPTIONS: { name: keyof MipoaAuthority; label: string }[] = [
  { name: "receive_inspect", label: "Receive and inspect confidential information" },
  { name: "make_presentation", label: "Make oral or written presentation of fact or argument" },
  { name: "sign_returns", label: "Sign returns" },
  { name: "enter_agreements", label: "Enter into agreements" },
];

const EMPTY_COPY_ROW: MipoaCopyRow = { tax_type: "", tax_period: "" };

const schema = Yup.object({
  generate_for: Yup.string().oneOf(["taxpayer", "spouse", "jointly"]).required(),
  auth_type: Yup.string().max(120, "Too long"),
  auth_years: yearRange,
  copy_rows: Yup.array()
    .of(
      Yup.object({
        tax_type: Yup.string().max(120, "Too long"),
        tax_period: yearRange,
      }),
    )
    .max(MAX_COPY_ROWS),
});

function toInitialValues(data: Record<string, unknown>): Form_mipoa_Data {
  const d = data as Partial<Form_mipoa_Data>;
  return {
    generate_for: d.generate_for ?? "taxpayer",
    representatives: d.representatives ?? [],
    authorization_start_date: d.authorization_start_date ?? "",
    authorization_expiration_date: d.authorization_expiration_date ?? "",
    representative_contact_name: d.representative_contact_name ?? "",
    revoke_all: d.revoke_all ?? false,
    revoke_matters: d.revoke_matters ?? false,
    revoked_type: d.revoked_type ?? "",
    revoked_years: d.revoked_years ?? "",
    revoke_copies: d.revoke_copies ?? false,
    revoked_type_2: d.revoked_type_2 ?? "",
    revoked_years_2: d.revoked_years_2 ?? "",
    authority: {
      receive_inspect: d.authority?.receive_inspect ?? false,
      make_presentation: d.authority?.make_presentation ?? false,
      sign_returns: d.authority?.sign_returns ?? false,
      enter_agreements: d.authority?.enter_agreements ?? false,
    },
    auth_type: d.auth_type ?? "",
    auth_years: d.auth_years ?? "",
    send_copies: d.send_copies ?? false,
    copy_rows:
      d.copy_rows && d.copy_rows.length > 0 ? d.copy_rows : [{ ...EMPTY_COPY_ROW }],
    signer: d.signer ?? "",
    signer_title: d.signer_title ?? "",
    signature_date: d.signature_date ?? "",
    spouse_signature_date: d.spouse_signature_date ?? "",
  };
}

function GenerateForField() {
  const [field, , helpers] = useField<MipoaGenerateFor>("generate_for");
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as MipoaGenerateFor)}
    >
      {GENERATE_FOR_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`gf-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`gf-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

/** A Formik-bound labelled text input (for the date + contact fields). */
function LabeledInput({
  name,
  label,
  placeholder,
}: {
  name: string;
  label: string;
  placeholder?: string;
}) {
  const [field] = useField(name);
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={name} className="text-sm font-medium">
        {label}
      </Label>
      <Input id={name} className="h-9" placeholder={placeholder} {...field} />
    </div>
  );
}

/**
 * Part 2 — revoke prior authorizations. Three independent checkboxes; the
 * matters/copies ones reveal a tax-type / period pair when checked.
 */
function RevokeSection() {
  const { values } = useFormikContext<Form_mipoa_Data>();
  return (
    <Card className="gap-4 p-6">
      <SectionHeading>Part 2 — Revoke Prior Authorizations</SectionHeading>
      <div className="grid gap-4">
        <FormikCheckbox
          name="revoke_all"
          label="I revoke all prior authorizations. I will represent myself."
        />
        <div className="flex flex-col gap-3">
          <FormikCheckbox
            name="revoke_matters"
            label="I revoke prior authorizations in the matter or dispute listed here. I will represent myself."
          />
          {values.revoke_matters && (
            <div className="ml-6 grid gap-3 sm:grid-cols-2">
              <FormikCell name="revoked_type" placeholder="Tax type / debt or fee" aria-label="Revoked tax type" />
              <FormikCell name="revoked_years" placeholder="Tax year(s) / period(s)" aria-label="Revoked tax years" />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-3">
          <FormikCheckbox
            name="revoke_copies"
            label="I revoke prior authorizations directing Treasury to send copies to my representative for the dispute(s) listed here."
          />
          {values.revoke_copies && (
            <div className="ml-6 grid gap-3 sm:grid-cols-2">
              <FormikCell name="revoked_type_2" placeholder="Tax type / debt or fee" aria-label="Revoked copies tax type" />
              <FormikCell name="revoked_years_2" placeholder="Tax year(s) / period(s)" aria-label="Revoked copies tax years" />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

/**
 * Part 5 — direct Treasury to send copies of notices/letters to the
 * representative, with up to three tax-type / tax-period rows (revealed when the
 * checkbox is on).
 */
function CopiesSection() {
  const { values } = useFormikContext<Form_mipoa_Data>();
  const rows = values.copy_rows;
  return (
    <Card className="gap-4 p-6">
      <SectionHeading>Part 5 — Send Copies to Representative</SectionHeading>
      <FormikCheckbox
        name="send_copies"
        label="Direct Treasury to send a copy of all future notices and letters for the disputes below to the representative named in Part 3."
      />
      {values.send_copies && (
        <FieldArray name="copy_rows">
          {(helpers) => (
            <div className="flex flex-col gap-3">
              <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tax Type / Debt or Fee</TableHead>
                      <TableHead className="w-56">Tax Year(s) / Period(s)</TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((_row, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <FormikCell
                            name={`copy_rows.${i}.tax_type`}
                            placeholder="e.g. Individual Income Tax"
                            aria-label={`Copy row tax type ${i + 1}`}
                          />
                        </TableCell>
                        <TableCell>
                          <FormikCell
                            name={`copy_rows.${i}.tax_period`}
                            placeholder="e.g. 2021, 2022"
                            aria-label={`Copy row tax period ${i + 1}`}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Remove copy row ${i + 1}`}
                            disabled={rows.length === 1}
                            onClick={() => helpers.remove(i)}
                          >
                            <Trash2 className="text-muted-foreground size-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
              <div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={rows.length >= MAX_COPY_ROWS}
                  onClick={() => helpers.push({ ...EMPTY_COPY_ROW })}
                >
                  <Plus /> Add copy row
                </Button>
                {rows.length >= MAX_COPY_ROWS && (
                  <span className="text-muted-foreground ml-3 text-xs">
                    Form 151 fits up to {MAX_COPY_ROWS} copy rows.
                  </span>
                )}
              </div>
            </div>
          )}
        </FieldArray>
      )}
    </Card>
  );
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Michigan Form 151 (Authorized Representative Declaration — Power of
 * Attorney): a single-representative POA with a revoke section (Part 2), the
 * four "type of authority" toggles + a matter row (Part 4), and a send-copies
 * section (Part 5). Taxpayer + representative identity are filled server-side
 * from the Client / Practitioner records.
 */
export function FormMipoaEditor({ client, submission, onSubmissionChange }: Props) {
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Card className="gap-6 p-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-foreground font-semibold">Generate for</span>
              <GenerateForField />
            </div>
            <div className="text-muted-foreground text-sm">
              <p className="text-foreground font-medium">{client.name}</p>
              {address && <p>{address}</p>}
            </div>
          </div>
        </Card>

        <RevokeSection />

        <section className="flex flex-col gap-4">
          <SectionHeading>Part 3 — Representative</SectionHeading>
          <PoaRepresentativesTable />
          <div className="grid gap-4 sm:grid-cols-3">
            <LabeledInput
              name="representative_contact_name"
              label="Contact Name"
              placeholder="Optional"
            />
            <LabeledInput
              name="authorization_start_date"
              label="Authorization Start Date"
              placeholder="MM/DD/YYYY"
            />
            <LabeledInput
              name="authorization_expiration_date"
              label="Authorization Expiration Date"
              placeholder="MM/DD/YYYY"
            />
          </div>
        </section>

        <Card className="gap-4 p-6">
          <SectionHeading>Part 4 — Type of Authority</SectionHeading>
          <div className="grid gap-3">
            {AUTHORITY_OPTIONS.map((o) => (
              <FormikCheckbox key={o.name} name={`authority.${o.name}`} label={o.label} />
            ))}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="auth_type" className="text-sm font-medium">
                Tax Type / Debt or Fee
              </Label>
              <FormikCell name="auth_type" placeholder="e.g. Individual Income Tax" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="auth_years" className="text-sm font-medium">
                Tax Year(s) / Period(s)
              </Label>
              <FormikCell name="auth_years" placeholder="e.g. 2021, 2022" />
            </div>
          </div>
        </Card>

        <CopiesSection />

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
